import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { ArrowLeft, Mail, Send, MapPin, Phone } from 'lucide-react';
import { toast } from 'sonner';

export default function ContactPage() {
  const [form, setForm] = useState({ nom: '', prenom: '', pays: '', age: '', email: '', telephone: '', message: '' });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.nom || !form.prenom || !form.email || !form.message) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    setSubmitting(true);
    // Send via mailto
    const subject = encodeURIComponent(`[MedSynapse] Message de ${form.prenom} ${form.nom}`);
    const body = encodeURIComponent(
      `Nom: ${form.nom}\nPrenom: ${form.prenom}\nPays: ${form.pays || 'Non renseigne'}\nAge: ${form.age || 'Non renseigne'}\nEmail: ${form.email}\nTelephone: ${form.telephone || 'Non renseigne'}\n\nMessage:\n${form.message}`
    );
    window.location.href = `mailto:ceo@openmeta.fr?subject=${subject}&body=${body}`;
    toast.success('Redirection vers votre client email...');
    setSubmitting(false);
  };

  return (
    <div className="mx-auto max-w-4xl px-3 sm:px-4 lg:px-6 py-4 sm:py-6">
      <Link to="/" className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors duration-150">
        <ArrowLeft className="w-4 h-4" /> Retour
      </Link>

      <div className="grid grid-cols-1 md:grid-cols-[1fr_300px] gap-6">
        <Card className="p-6 sm:p-8">
          <div className="flex items-center gap-3 mb-6">
            <Mail className="w-8 h-8 text-[hsl(var(--primary))]" />
            <h1 className="font-heading text-3xl font-bold">Contact</h1>
          </div>
          <p className="text-muted-foreground mb-6">Envoyez-nous un message a <a href="mailto:ceo@openmeta.fr" className="text-[hsl(var(--primary))] hover:underline">ceo@openmeta.fr</a></p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Nom <span className="text-[hsl(var(--destructive))]">*</span></label>
                <Input value={form.nom} onChange={(e) => setForm({...form, nom: e.target.value})} placeholder="Nom" required />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Prenom <span className="text-[hsl(var(--destructive))]">*</span></label>
                <Input value={form.prenom} onChange={(e) => setForm({...form, prenom: e.target.value})} placeholder="Prenom" required />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Pays</label>
                <Input value={form.pays} onChange={(e) => setForm({...form, pays: e.target.value})} placeholder="France" />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Age</label>
                <Input value={form.age} onChange={(e) => setForm({...form, age: e.target.value})} placeholder="30" type="number" />
              </div>
            </div>

            <div>
              <label className="text-sm font-medium mb-1 block">E-mail <span className="text-[hsl(var(--destructive))]">*</span></label>
              <Input value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} placeholder="votre@email.com" type="email" required />
            </div>

            <div>
              <label className="text-sm font-medium mb-1 block">Telephone</label>
              <Input value={form.telephone} onChange={(e) => setForm({...form, telephone: e.target.value})} placeholder="+33 6 00 00 00 00" />
            </div>

            <div>
              <label className="text-sm font-medium mb-1 block">Descriptif de la demande <span className="text-[hsl(var(--destructive))]">*</span></label>
              <Textarea value={form.message} onChange={(e) => setForm({...form, message: e.target.value})} placeholder="Decrivez votre demande..." className="min-h-[120px]" required />
            </div>

            <p className="text-xs text-muted-foreground">* Champs obligatoires</p>

            <Button type="submit" disabled={submitting} className="w-full gap-2">
              <Send className="w-4 h-4" />
              {submitting ? 'Envoi...' : 'Envoyer'}
            </Button>
          </form>
        </Card>

        {/* Sidebar */}
        <div className="space-y-4">
          <Card className="p-4">
            <h3 className="font-heading font-semibold text-sm mb-3">Informations</h3>
            <div className="space-y-3">
              <div className="flex items-start gap-2">
                <Mail className="w-4 h-4 text-[hsl(var(--primary))] mt-0.5" />
                <div>
                  <p className="text-xs font-medium">Email</p>
                  <a href="mailto:ceo@openmeta.fr" className="text-xs text-[hsl(var(--primary))] hover:underline">ceo@openmeta.fr</a>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-[hsl(var(--primary))] mt-0.5" />
                <div>
                  <p className="text-xs font-medium">Adresse</p>
                  <p className="text-xs text-muted-foreground">Centre de l'innovation technologique</p>
                  <p className="text-xs text-muted-foreground">61330 Ceauce, FRANCE</p>
                </div>
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <h3 className="font-heading font-semibold text-sm mb-2">Liens utiles</h3>
            <div className="space-y-1">
              <Link to="/cgu" className="block text-xs text-[hsl(var(--primary))] hover:underline">Conditions Generales d'Utilisation</Link>
              <Link to="/confidentialite" className="block text-xs text-[hsl(var(--primary))] hover:underline">Politique de Confidentialite</Link>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
